/**
 * AI Error Code Library
 * Maps error messages to user-friendly error codes
 */

export interface ErrorCodeResponse {
  error_code: string;
  user_message: string;
}

// Error code mapping
const ERROR_PATTERNS: { pattern: RegExp; code: string }[] = [
  // Provider availability errors
  { pattern: /provider.*not available.*no api key/i, code: 'ERR_001' },
  { pattern: /provider.*not available.*credits exhausted/i, code: 'ERR_002' },
  { pattern: /no ai providers are available/i, code: 'ERR_003' },
  { pattern: /all ai providers failed/i, code: 'ERR_004' },
  
  // API/Network errors
  { pattern: /http.*error/i, code: 'ERR_101' },
  { pattern: /connection.*error/i, code: 'ERR_102' },
  { pattern: /timeout.*error/i, code: 'ERR_103' },
  { pattern: /network.*error/i, code: 'ERR_104' },
  { pattern: /failed to fetch/i, code: 'ERR_104' },
  
  // Authentication errors
  { pattern: /token.*invalid/i, code: 'ERR_201' },
  { pattern: /token.*expired/i, code: 'ERR_202' },
  { pattern: /authentication.*failed/i, code: 'ERR_203' },
  { pattern: /http\s*401/i, code: 'ERR_204' },
  { pattern: /http\s*403/i, code: 'ERR_204' },
  
  // Data errors
  { pattern: /no data available/i, code: 'ERR_301' },
  { pattern: /data.*not found/i, code: 'ERR_302' },
  { pattern: /invalid.*data/i, code: 'ERR_303' },
  
  // Service errors
  { pattern: /service.*unavailable/i, code: 'ERR_401' },
  { pattern: /service.*error/i, code: 'ERR_402' },
  { pattern: /internal.*error/i, code: 'ERR_403' },
];

// User-friendly error messages
const ERROR_MESSAGES: Record<string, string> = {
  ERR_001: 'AI service temporarily unavailable. Please try again later.',
  ERR_002: 'AI service quota exceeded. Please try again later.',
  ERR_003: 'AI service not configured. Please contact support.',
  ERR_004: 'AI service temporarily unavailable. Please try again later.',
  ERR_101: 'Network error. Please check your connection and try again.',
  ERR_102: 'AI service (Ollama) is unreachable. Please try again in a moment or check that the service is running.',
  ERR_103: 'Request timeout. Please try again.',
  ERR_104: 'Network error. Please try again.',
  ERR_201: 'Authentication error. Please refresh the page.',
  ERR_202: 'Session expired. Please refresh the page.',
  ERR_203: 'Authentication failed. Please refresh the page.',
  ERR_204: 'Unauthorized access. Please refresh the page.',
  ERR_301: 'No data available for this request.',
  ERR_302: 'Data not found. Please try a different query.',
  ERR_303: 'Invalid data format. Please try again.',
  ERR_401: 'Service temporarily unavailable. Please try again later.',
  ERR_402: 'Service error. Please try again later.',
  ERR_403: 'Internal error. Please try again later.',
  ERR_999: 'An error occurred. Please try again later.',
};

/**
 * Get error code from error message
 */
export function getErrorCode(errorMessage: string | null | undefined): string {
  if (!errorMessage) {
    return 'ERR_999';
  }
  
  const errorLower = errorMessage.toLowerCase();
  
  // Check each pattern
  for (const { pattern, code } of ERROR_PATTERNS) {
    if (pattern.test(errorLower)) {
      return code;
    }
  }
  
  // Default fallback
  return 'ERR_999';
}

/**
 * Get user-friendly error message from error code
 */
export function getUserFriendlyMessage(errorCode: string): string {
  return ERROR_MESSAGES[errorCode] || ERROR_MESSAGES['ERR_999'];
}

/**
 * Encrypt error message - return ONLY encrypted code, no user messages
 */
export function encryptError(errorMessage: string | null | undefined): ErrorCodeResponse {
  const errorCode = getErrorCode(errorMessage);
  
  // Generate encrypted reference code (for internal tracking only)
  // Simple hash-based encryption for reference
  let errorHash = '';
  if (errorMessage) {
    // Simple hash (in production, use proper crypto)
    let hash = 0;
    for (let i = 0; i < errorMessage.length; i++) {
      const char = errorMessage.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    errorHash = Math.abs(hash).toString(16).substring(0, 8).toUpperCase();
  }
  
  const encryptedRef = `${errorCode}-${errorHash}`;
  
  return {
    error_code: encryptedRef,
    user_message: getUserFriendlyMessage(errorCode.split("-")[0] || errorCode),
  };
}

